/**
 * The next-period estimate, which depends on BOTH contexts.
 *
 * Prediction is a function of the entry set (EntryContext) and of whether the
 * user has suspended estimates (SettingsContext, FR-412). Rather than have
 * EntryProvider reach into SettingsProvider — which would make their mounting
 * order load-bearing and hide the dependency — the two are composed here, where
 * it is visible in one place.
 *
 * Cheap to call from several screens: `predictNextPeriod` is pure and operates
 * on at most six cycle lengths.
 */
import { useMemo } from 'react'
import { predictNextPeriod, type Prediction } from '@domain/index'
import { useEntries } from './EntryContext'
import { useSettings } from './SettingsContext'

export function usePrediction(): Prediction {
  const { derived } = useEntries()
  const { settings } = useSettings()
  const suspended = settings.predictions.suspended

  return useMemo(
    () => predictNextPeriod(derived, { suspended }),
    [derived, suspended],
  )
}
